import {
  TResourceDetailColSpan,
  TResourceDetailItem,
} from '@/shared/components/ui/resource-detail/item/resource-detail-item.types';
import {
  COMPACT_ITEMS_PER_ROW,
  COMPACT_MIN_ITEMS,
} from '@/shared/components/ui/resource-detail/resource-detail.constants';

// Clamped rather than rejected: an over-wide item should render full-width,
// never take down the page it is rendered on.
export const getResourceDetailItemSize = (
  itemsPerRow: number,
  colSpan?: TResourceDetailColSpan,
): number => Math.min(colSpan ?? 1, itemsPerRow);

export const getCompactItems = (
  items: TResourceDetailItem[],
): TResourceDetailItem[] => items.filter((item) => item.showOnlyOn !== 'full');

export const getFullItems = (
  items: TResourceDetailItem[],
): TResourceDetailItem[] =>
  items.filter((item) => item.showOnlyOn !== 'compact');

export const getCompactColumns = (items: TResourceDetailItem[]): number =>
  getCompactItems(items).reduce(
    (total, item) => total + (item.colSpan ?? 1),
    0,
  );

// Compact is a single non-wrapping row, so anything past the column budget is
// dropped. Kept as a prefix so the visible items stay in author order.
export const getFittingCompactItems = (
  items: TResourceDetailItem[],
  compactItemsPerRow: number = COMPACT_ITEMS_PER_ROW,
): TResourceDetailItem[] => {
  const fitting: TResourceDetailItem[] = [];
  let usedColumns = 0;

  for (const item of getCompactItems(items)) {
    const span = item.colSpan ?? 1;
    if (usedColumns + span > compactItemsPerRow) break;
    fitting.push(item);
    usedColumns += span;
  }

  return fitting;
};

// Only offer compact mode when the full view is dense enough to be worth
// collapsing AND the compact items fit the row without being trimmed. Callers
// that pass a custom compactItemsPerRow to ResourceDetailContent must pass the
// same value here, or the collapsed header will silently drop items.
export const supportsCompact = (
  items: TResourceDetailItem[],
  compactItemsPerRow: number = COMPACT_ITEMS_PER_ROW,
): boolean =>
  getFullItems(items).length >= COMPACT_MIN_ITEMS &&
  getCompactColumns(items) <= compactItemsPerRow;
